import { useCallback } from 'react';
import en from '../../locales/en.json';
import bn from '../../locales/bn.json';
import { useLocaleStore } from '../stores/localeStore';
import type { SupportedLocale } from '../../domain/formatters/currency';

type Messages = typeof en;

const CATALOGS: Record<SupportedLocale, Messages> = { en, bn };

function lookup(catalog: Messages, key: string): string | undefined {
  let node: unknown = catalog;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : undefined;
}

export interface Translation {
  t: (key: string, vars?: Record<string, string | number>) => string;
  locale: SupportedLocale;
}

/** EN/BN translation hook (W01). Missing keys fall back to English, then to the key itself. */
export function useTranslation(): Translation {
  const locale = useLocaleStore((s) => s.locale);
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      let raw = lookup(CATALOGS[locale], key) ?? lookup(CATALOGS.en, key) ?? key;
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          raw = raw.replaceAll(`{{${name}}}`, String(value));
        }
      }
      return raw;
    },
    [locale],
  );
  return { t, locale };
}
